import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import Checkbox from './Checkbox';
import { trapPanelTab, useAnchoredPanel } from './use-anchored-panel';

// The column chooser: a list of every column of the table, in the order of
// the table, with a checkbox each, as in Tercero. The reader adds or removes a
// column, and moves a column up (to the left) or down (to the right) with the
// two arrow buttons. The buttons are the keyboard way to reorder; a drag of a
// header is the pointer way (SEP-298). The panel opens under the chooser
// button, aligned to its right edge, because the button sits at the right end
// of the header.

export interface ColumnChooserItem {
    id: string;
    label: string;
    visible: boolean;
    /** false when the column cannot be hidden, or when it is the last one. */
    canToggle: boolean;
    /** A visible column that can move one place to the left. */
    canMoveUp?: boolean;
    /** A visible column that can move one place to the right. */
    canMoveDown?: boolean;
}

export interface ColumnChooserPopoverProps {
    anchor: HTMLElement;
    tableLabel: string;
    items: readonly ColumnChooserItem[];
    isDefault: boolean;
    onToggle: (id: string) => void;
    /** Move a column one place: -1 to the left, +1 to the right. */
    onMove?: (id: string, step: -1 | 1) => void;
    onShowAll: () => void;
    onReset: () => void;
    onClose: () => void;
}

/** The attribute that marks the chooser button. */
export const CHOOSER_TRIGGER_ATTRIBUTE = 'data-column-chooser-trigger';

const PANEL_WIDTH = 256;
// A long list gets a search box. A short one does not need it.
const SEARCH_FROM = 9;

const MOVE_CLASS =
    'grid h-5 w-5 place-items-center rounded-[3px] text-[rgb(var(--color-text-tertiary))] hover:bg-[rgb(var(--color-surface-hover)/0.06)] hover:text-[rgb(var(--color-text-primary))] disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)] pointer-coarse:h-8 pointer-coarse:w-8';

const LINK_CLASS =
    'text-caption text-[rgb(var(--color-accent))] hover:underline disabled:text-[rgb(var(--color-text-tertiary))] disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)] rounded';

export default function ColumnChooserPopover({
    anchor,
    tableLabel,
    items,
    isDefault,
    onToggle,
    onMove,
    onShowAll,
    onReset,
    onClose,
}: ColumnChooserPopoverProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [search, setSearch] = useState('');
    const pos = useAnchoredPanel({
        anchor,
        panelRef,
        width: PANEL_WIDTH,
        align: 'end',
        triggerAttribute: CHOOSER_TRIGGER_ATTRIBUTE,
        onClose,
    });

    useEffect(() => {
        panelRef.current?.querySelector<HTMLElement>('input')?.focus();
        // Open once. Later edits must not pull focus back.
    }, []);

    const closeWithFocus = () => {
        anchor.querySelector<HTMLElement>(`[${CHOOSER_TRIGGER_ATTRIBUTE}]`)?.focus();
        onClose();
    };

    const needle = search.trim().toLowerCase();
    const shown = needle ? items.filter((i) => i.label.toLowerCase().includes(needle)) : items;
    const visibleCount = items.filter((i) => i.visible).length;
    const allVisible = visibleCount === items.length;

    const onPanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        // Alt+ArrowUp goes to the parent page. Inside the panel it must not.
        if (e.altKey && e.key === 'ArrowUp') e.stopPropagation();
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closeWithFocus();
            return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const boxes = Array.from(
                listRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ?? [],
            );
            if (boxes.length === 0) return;
            e.preventDefault();
            const at = boxes.indexOf(document.activeElement as HTMLInputElement);
            const next = at < 0 ? 0 : at + (e.key === 'ArrowDown' ? 1 : -1);
            boxes[Math.max(0, Math.min(boxes.length - 1, next))]?.focus();
            return;
        }
        trapPanelTab(e, panelRef.current);
    };

    const panel = (
        <div
            ref={panelRef}
            role="dialog"
            aria-label={`Columns of ${tableLabel}`}
            onKeyDown={onPanelKeyDown}
            style={{ top: pos.top, left: pos.left }}
            className={cn(
                'fixed z-[80] flex w-[256px] max-h-[min(420px,calc(100vh-16px))] flex-col',
                // The raised surface and the popover shadow keep the panel apart
                // from the rows under it, in light and in dark.
                'rounded-card border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-raised))]',
                'text-annotation text-[rgb(var(--color-text-primary))] shadow-[var(--septima-popover-shadow)]',
            )}
        >
            <div className="flex h-7 flex-none items-center justify-between gap-2 border-b border-[rgb(var(--color-divider))] px-2">
                <span className="font-medium">Columns</span>
                <span className="tabular-nums text-[rgb(var(--color-text-tertiary))]">
                    {visibleCount} of {items.length}
                </span>
            </div>
            {items.length >= SEARCH_FROM && (
                <div className="flex-none px-2 pt-2">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search columns"
                        aria-label="Search columns"
                        className={cn(
                            'h-7 w-full min-w-0 rounded-button border border-[rgb(var(--color-divider))]',
                            'bg-[rgb(var(--color-surface))] px-2 text-annotation text-[rgb(var(--color-text-primary))]',
                            'placeholder:text-[rgb(var(--color-text-tertiary))]',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                            'pointer-coarse:h-8',
                        )}
                    />
                </div>
            )}
            <div ref={listRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto p-1">
                {shown.map((item) => (
                    <label
                        key={item.id}
                        className={cn(
                            'flex h-6 flex-none items-center gap-2 rounded px-1.5 pointer-coarse:h-8',
                            item.canToggle
                                ? 'cursor-pointer hover:bg-[rgb(var(--color-surface-hover)/0.05)]'
                                : 'text-[rgb(var(--color-text-tertiary))]',
                        )}
                    >
                        <Checkbox
                            checked={item.visible}
                            disabled={!item.canToggle}
                            onChange={() => onToggle(item.id)}
                            aria-label={item.label}
                        />
                        <span className="min-w-0 flex-1 truncate" title={item.label}>
                            {item.label}
                        </span>
                        {onMove && item.visible && (item.canMoveUp || item.canMoveDown) ? (
                            <span className="flex flex-none items-center">
                                <button
                                    type="button"
                                    disabled={!item.canMoveUp}
                                    aria-label={`Move ${item.label} left`}
                                    title="Move left"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onMove(item.id, -1);
                                    }}
                                    className={MOVE_CLASS}
                                >
                                    <ChevronUp className="h-3 w-3" />
                                </button>
                                <button
                                    type="button"
                                    disabled={!item.canMoveDown}
                                    aria-label={`Move ${item.label} right`}
                                    title="Move right"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onMove(item.id, 1);
                                    }}
                                    className={MOVE_CLASS}
                                >
                                    <ChevronDown className="h-3 w-3" />
                                </button>
                            </span>
                        ) : null}
                    </label>
                ))}
                {shown.length === 0 && (
                    <div className="px-1.5 py-1 text-[rgb(var(--color-text-tertiary))]">
                        No matching columns
                    </div>
                )}
            </div>
            <div className="flex h-7 flex-none items-center justify-between gap-2 border-t border-[rgb(var(--color-divider))] px-2">
                <button
                    type="button"
                    onClick={onShowAll}
                    disabled={allVisible}
                    className={LINK_CLASS}
                >
                    Show all
                </button>
                <button type="button" onClick={onReset} disabled={isDefault} className={LINK_CLASS}>
                    Reset
                </button>
            </div>
        </div>
    );

    return createPortal(panel, document.body);
}
